import React, { useEffect, useState } from 'react';
import { DollarSign, Hash, Cpu, BarChart3, Eye, Layers, Link2, Clock, CheckCircle2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useUsageStore } from '@/stores/agents/usage-store';
import { tracesApi, resourcesApi, ThreadSummary } from '@/services/api/agentApi';

const StatCard: React.FC<{ label: string; value: string; subtitle?: string; icon: React.ReactNode; color?: string }> = ({
  label, value, subtitle, icon, color = 'text-primary',
}) => (
  <div className="border border-border rounded-lg p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={color}>{icon}</span>
    </div>
    <p className="text-2xl font-bold">{value}</p>
    {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
  </div>
);

const periodOptions = [7, 14, 30] as const;

// ─── Visual Editor Metrics Section ──────────────────────────────────────────

interface VisualEditorMetrics {
  totalGenerations: number;
  avgGenerationTimeMs: number;
  pipelineDistribution: { name: string; count: number }[];
  agentCostBreakdown: { agent: string; cost: number; tokens: number; calls: number }[];
  dataBindingSuccessRate: number;
}

const PIPELINE_TYPE_COLORS = ['#6366f1', '#8b5cf6', '#3b82f6', '#06b6d4'];

const VisualEditorMetricsSection: React.FC<{ days: number }> = ({ days }) => {
  const [metrics, setMetrics] = useState<VisualEditorMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchMetrics = async () => {
      setIsLoading(true);
      try {
        // Fetch trace stats and thread listings to derive visual editor metrics
        const [statsResult, threadsResult] = await Promise.all([
          tracesApi.getStats(days),
          tracesApi.listThreads({ limit: 200 }),
        ]);

        const allThreads: ThreadSummary[] = threadsResult.data.threads || [];
        const veThreads = allThreads.filter((t) => t.initiator_detail === 'visual-editor');

        // Pipeline type distribution: infer from agent_route or title patterns
        const pipelineCounts: Record<string, number> = {
          'Template-First': 0,
          'Data-First': 0,
          'Single-Agent': 0,
          'Multi-Agent': 0,
        };
        let totalDuration = 0;
        let dataBindingTotal = 0;
        let dataBindingSuccess = 0;

        for (const t of veThreads) {
          totalDuration += t.total_duration_ms || 0;
          const route = (t.agent_route || '').toLowerCase();
          const title = (t.title || '').toLowerCase();

          if (route.includes('data') || title.includes('data-first')) {
            pipelineCounts['Data-First']++;
          } else if (title.includes('template') || route.includes('template')) {
            pipelineCounts['Template-First']++;
          } else if (t.total_llm_calls <= 2) {
            pipelineCounts['Single-Agent']++;
          } else {
            pipelineCounts['Multi-Agent']++;
          }

          // Estimate data binding: if there are data spans, count as attempted
          if (t.total_spans > 3) {
            dataBindingTotal++;
            if (t.status === 'completed') dataBindingSuccess++;
          }
        }

        // Per-agent cost breakdown from stats
        const byAgent = statsResult.data.byAgent || {};
        const agentCostBreakdown = Object.entries(byAgent).map(([agent, count]) => ({
          agent,
          cost: 0, // We approximate from spending data
          tokens: 0,
          calls: count as number,
        }));

        // Try to enrich with spending data
        try {
          const usageResult = await resourcesApi.getUsage({ days: String(days) });
          if (Array.isArray(usageResult.data)) {
            for (const entry of usageResult.data) {
              const match = agentCostBreakdown.find(
                (a) => a.agent.toLowerCase() === (entry.agent || entry.model || '').toLowerCase()
              );
              if (match) {
                match.cost += entry.cost_usd || entry.cost || 0;
                match.tokens += entry.total_tokens || entry.tokens || 0;
              }
            }
          }
        } catch {
          // Usage detail may not be available
        }

        if (!cancelled) {
          setMetrics({
            totalGenerations: veThreads.length,
            avgGenerationTimeMs: veThreads.length > 0 ? totalDuration / veThreads.length : 0,
            pipelineDistribution: Object.entries(pipelineCounts)
              .filter(([, count]) => count > 0)
              .map(([name, count]) => ({ name, count })),
            agentCostBreakdown,
            dataBindingSuccessRate:
              dataBindingTotal > 0 ? (dataBindingSuccess / dataBindingTotal) * 100 : 0,
          });
        }
      } catch {
        // Silently degrade if metrics fetch fails
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchMetrics();
    return () => { cancelled = true; };
  }, [days]);

  if (isLoading && !metrics) {
    return (
      <div className="border border-border rounded-lg p-4">
        <div className="text-sm text-muted-foreground">Loading visual editor metrics...</div>
      </div>
    );
  }

  if (!metrics || metrics.totalGenerations === 0) {
    return (
      <div className="border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Eye className="h-4 w-4 text-pink-500" />
          <h3 className="text-sm font-medium">Visual Editor</h3>
        </div>
        <p className="text-xs text-muted-foreground">No visual editor usage data in the last {days} days.</p>
      </div>
    );
  }

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-accent/50 transition-colors"
      >
        <Eye className="h-4 w-4 text-pink-500" />
        <h3 className="text-sm font-medium flex-1 text-left">Visual Editor</h3>
        <span className="text-xs text-muted-foreground">{metrics.totalGenerations} generation{metrics.totalGenerations !== 1 ? 's' : ''}</span>
        {isExpanded ? (
          <svg className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
        ) : (
          <svg className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Summary row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-muted/30 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Layers className="h-3.5 w-3.5 text-indigo-500" />
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Generations</span>
              </div>
              <p className="text-lg font-bold">{metrics.totalGenerations}</p>
            </div>
            <div className="bg-muted/30 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Clock className="h-3.5 w-3.5 text-blue-500" />
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Avg Time</span>
              </div>
              <p className="text-lg font-bold">{formatDuration(metrics.avgGenerationTimeMs)}</p>
            </div>
            <div className="bg-muted/30 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Link2 className="h-3.5 w-3.5 text-cyan-500" />
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Data Binding</span>
              </div>
              <p className="text-lg font-bold">{metrics.dataBindingSuccessRate.toFixed(0)}%</p>
            </div>
            <div className="bg-muted/30 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Success</span>
              </div>
              <p className="text-lg font-bold">{metrics.dataBindingSuccessRate.toFixed(0)}%</p>
            </div>
          </div>

          {/* Pipeline Type Distribution */}
          {metrics.pipelineDistribution.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-muted-foreground mb-2">Pipeline Type Distribution</h4>
              <div className="flex gap-2 flex-wrap">
                {metrics.pipelineDistribution.map((item, idx) => (
                  <div
                    key={item.name}
                    className="flex items-center gap-2 border border-border rounded-lg px-3 py-2"
                  >
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: PIPELINE_TYPE_COLORS[idx % PIPELINE_TYPE_COLORS.length] }}
                    />
                    <div>
                      <p className="text-xs font-medium">{item.name}</p>
                      <p className="text-[10px] text-muted-foreground">{item.count} request{item.count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-agent cost breakdown */}
          {metrics.agentCostBreakdown.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-muted-foreground mb-2">Per-Agent Breakdown</h4>
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-2 font-medium">Agent</th>
                      <th className="text-right p-2 font-medium">Calls</th>
                      <th className="text-right p-2 font-medium">Tokens</th>
                      <th className="text-right p-2 font-medium">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.agentCostBreakdown.map((row) => (
                      <tr key={row.agent} className="border-t border-border">
                        <td className="p-2 font-mono text-[10px]">{row.agent}</td>
                        <td className="p-2 text-right">{row.calls}</td>
                        <td className="p-2 text-right">
                          {row.tokens >= 1000
                            ? `${(row.tokens / 1000).toFixed(1)}k`
                            : row.tokens || '-'}
                        </td>
                        <td className="p-2 text-right">
                          {row.cost > 0 ? `$${row.cost.toFixed(4)}` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const UsageTab: React.FC = () => {
  const { spending, isLoading, error, days, fetchSpending, setDays } = useUsageStore();

  useEffect(() => {
    fetchSpending();
  }, []);

  if (isLoading && !spending) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-muted-foreground">Loading usage data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-destructive">{error}</p>
          <button onClick={() => fetchSpending()} className="text-sm text-primary hover:underline">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!spending || (spending.totals.requestCount === 0 && spending.daily.length === 0)) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center space-y-4">
          <BarChart3 className="h-16 w-16 mx-auto text-muted-foreground/40" />
          <h3 className="text-lg font-medium text-muted-foreground">No Usage Data</h3>
          <p className="text-sm text-muted-foreground/70 max-w-md">
            Agent usage data will appear here once agents start processing requests.
          </p>
        </div>
      </div>
    );
  }

  const { totals, daily, byModel } = spending;

  const formatTokens = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return String(n);
  };

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-muted-foreground">Agent Spending</h2>
        <div className="flex gap-1 border border-border rounded-lg p-0.5">
          {periodOptions.map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                days === d
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Total Spent"
          value={`$${totals.totalCost.toFixed(4)}`}
          subtitle={`Last ${days} days`}
          icon={<DollarSign className="h-5 w-5" />}
          color="text-amber-500"
        />
        <StatCard
          label="Total Requests"
          value={totals.requestCount.toLocaleString()}
          subtitle={`${(totals.requestCount / days).toFixed(1)} avg/day`}
          icon={<Hash className="h-5 w-5" />}
        />
        <StatCard
          label="Total Tokens"
          value={formatTokens(totals.totalTokens)}
          subtitle={`${formatTokens(Math.round(totals.totalTokens / days))} avg/day`}
          icon={<Cpu className="h-5 w-5" />}
        />
      </div>

      {/* Daily spending chart */}
      {daily.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-3">Daily Spending</h3>
          <div className="border border-border rounded-lg p-4" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d: string) => d.slice(5)} // MM-DD
                  className="text-xs"
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                />
                <YAxis
                  tickFormatter={(v: number) => `$${v.toFixed(3)}`}
                  className="text-xs"
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                  width={60}
                />
                <Tooltip
                  formatter={(value: number) => [`$${value.toFixed(4)}`, 'Cost']}
                  labelFormatter={(label: string) => `Date: ${label}`}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    color: 'hsl(var(--popover-foreground))',
                  }}
                />
                <Bar dataKey="cost" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Per-model cost table */}
      {byModel.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-3">Cost by Model</h3>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">Model</th>
                  <th className="text-right p-3 font-medium">Cost</th>
                  <th className="text-right p-3 font-medium">Requests</th>
                  <th className="text-right p-3 font-medium">Tokens</th>
                </tr>
              </thead>
              <tbody>
                {byModel.map((m) => (
                  <tr key={m.model} className="border-t border-border">
                    <td className="p-3 font-mono text-xs">{m.model}</td>
                    <td className="p-3 text-right">${m.cost.toFixed(4)}</td>
                    <td className="p-3 text-right">{m.requests.toLocaleString()}</td>
                    <td className="p-3 text-right">{formatTokens(m.tokens)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Visual Editor Metrics */}
      <VisualEditorMetricsSection days={days} />
    </div>
  );
};
